import React, { useEffect, useRef, useState } from "react";

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire: () => void;
  action?: string;
}

const scriptId = "cloudflare-turnstile-script";

const loadTurnstileScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  });

export const Turnstile: React.FC<TurnstileProps> = ({ onVerify, onExpire, action = "contact" }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const isPrerendering = Boolean(window.__PRERENDERING__);

  useEffect(() => {
    let active = true;

    if (!siteKey || isPrerendering) {
      return undefined;
    }

    loadTurnstileScript()
      .then(() => {
        if (!active || !containerRef.current || !window.turnstile) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "light",
          language: "en",
          callback: onVerify,
          "expired-callback": onExpire,
          "error-callback": onExpire,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setHasError(true);
        setIsLoading(false);
        onExpire();
      });

    return () => {
      active = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [action, isPrerendering, onExpire, onVerify, siteKey]);

  if (!siteKey) {
    return <p className="contactMeForm__securityError">Security check is temporarily unavailable.</p>;
  }

  if (hasError) {
    return <p className="contactMeForm__securityError">Security check is temporarily unavailable.</p>;
  }

  return (
    <div className="contactMeForm__turnstileWrap">
      {isLoading && <p className="contactMeForm__securityStatus">Preparing secure form…</p>}
      <div className="contactMeForm__turnstile" ref={containerRef} />
    </div>
  );
};
